import { Center, Flex, forwardRef, Stack, Text, Tooltip } from '@chakra-ui/react'
import { AssessmentStepKey, GynStepKey, StageProgress } from '@hb/shared'
import React, { PropsWithChildren, useCallback, useMemo } from 'react'
import { useFormsView } from '../../contexts/assessments/FormsViewContext'

type UnderlineButtonProps = PropsWithChildren<{
  onClick: () => void
  width: number | string
  title: string
  isActive: boolean
  progress?: number
  isLast?: boolean
  isFirst?: boolean
  isVisible: boolean
  disabled?: boolean
}>

const UnderlineButtonProgress = ({
  progress,
  isFirst,
  isLast,
  color = 'green.300',
}: {
  progress?: number
  isFirst?: boolean
  isLast?: boolean
  color?: string
}) => (
  <Flex
    top="0"
    left="0"
    transition={'width 1500ms cubic-bezier(0.16, 1, 0.3, 1)'}
    height="100%"
    borderStartRadius={isFirst ? 8 : 0}
    borderEndRadius={isLast ? 8 : 0}
    overflow="hidden"
    pos="absolute"
    bg={color}
    width={`${progress}%`}
    zIndex={1}
  />
)

export const UnderlineButton = forwardRef<UnderlineButtonProps, 'div'>(
  (
    { onClick, children, width, isActive, isLast, isVisible, isFirst, title, progress, disabled },
    ref,
  ) => {
    let opacity = 1
    if (!isVisible) {
      opacity = 0
    } else if (disabled) {
      opacity = 0.7
    }
    return (
      <Center
        ref={ref}
        onClick={onClick}
        cursor="pointer"
        aria-label={title}
        opacity={opacity}
        transition="all 1000ms"
        pos="relative"
        height="40px"
        boxShadow="0 0 5px 0 rgba(0, 0, 0, 0.2)"
        borderStartRadius={isFirst ? 8 : 0}
        borderEndRadius={isLast ? 8 : 0}
        bg="whiteAlpha.800"
        _dark={{ bg: 'whiteAlpha.500' }}
        width={isVisible ? width : 0}>
        <UnderlineButtonProgress progress={progress} isFirst={isFirst} isLast={isLast} />
        <Flex
          bottom="0"
          transform={`translate(0, ${isActive ? 8 : -5}px)`}
          transition="all 1000ms cubic-bezier(0.16, 1, 0.3, 1)"
          opacity={isActive ? 1 : 0}
          // pl={isFirst ? 1 : 0}
          width="100%"
          height={'5px'}
          pos="absolute">
          <Flex
            bg="whiteAlpha.800"
            _dark={{ bg: 'whiteAlpha.500' }}
            height="100%"
            width="100%"
            boxShadow={isActive ? '0 0 5px 0 rgba(0, 0, 0, 0.2)' : 'none'}
            borderStartRadius={isFirst ? 'md' : 'none'}
            borderEndRadius={isLast ? 'md' : 'none'}
            pos="relative">
            <UnderlineButtonProgress
              color="green.200"
              progress={progress}
              isFirst={isFirst}
              isLast={isLast}
            />
          </Flex>
        </Flex>
        {children}
      </Center>
    )
  },
)

export interface StageProgressBarProps<StepKey extends AssessmentStepKey | GynStepKey> {
  id: StepKey
  isLast?: boolean
  isFirst?: boolean
  isActive: boolean
  index: number
  numItems: number
  stage: StageProgress
  disabled?: boolean
  onClick: () => void
}
export const StageProgressBar = <StepKey extends AssessmentStepKey | GynStepKey>({
  id,
  isLast,
  stage,
  onClick,
  isActive,
  numItems,
  isFirst,
}: StageProgressBarProps<StepKey>) => (
  <Tooltip
    colorScheme="green"
    bg="green.500"
    label={
      id === 'view'
        ? "When you submit the questionnaire, we'll look at your information and send you an assessment on your coverage"
        : 'After you view the assessment, you can choose to officially sign on for coverage'
    }
    isDisabled={stage.active}>
    <UnderlineButton
      isFirst={isFirst}
      isLast={isLast}
      isActive={isActive}
      isVisible={stage.visible}
      disabled={!stage.active}
      onClick={onClick}
      title={stage.name}
      width={`${100 / numItems}%`}
      progress={stage.progress}>
      <Text
        zIndex={1}
        color="#00000088"
        _dark={{ color: stage.progress ? 'white' : 'whiteAlpha.700' }}
        fontWeight={600}
        fontSize="sm"
        textAlign="center">
        {stage.name.toUpperCase()}
      </Text>
    </UnderlineButton>
  </Tooltip>
)

export const StageProgressView = <StepKey extends AssessmentStepKey | GynStepKey>({
  bars,
  stage,
  selectStage,
}: {
  bars: Record<StepKey, StageProgress>
  stage: StepKey
  selectStage: (k: StepKey) => void
}) => {
  const handleClick = useCallback(
    (key: StepKey) => {
      if (bars[key]?.active) {
        selectStage(key)
      }
    },
    [bars, selectStage],
  )

  const visibleIndices = useMemo(() => {
    let numVisible = 0
    const indices: number[] = []
    Object.values(bars).forEach(b => {
      indices.push(numVisible)
      if ((b as StageProgress).visible) {
        numVisible++
      }
    })
    return indices
  }, [bars])

  const numBars = useMemo(
    () => Object.values(bars).filter(b => (b as StageProgress).visible).length,
    [bars],
  )
  return (
    <Stack mt={2} mb={3} direction="row" spacing={1} w="100%" borderRadius="md">
      {Object.entries(bars).map(([key, b], i) => (
        <StageProgressBar
          onClick={() => handleClick(key as StepKey)}
          key={key}
          numItems={numBars}
          isActive={key === stage}
          stage={b as StageProgress}
          id={key as StepKey}
          index={i}
          isFirst={visibleIndices[i] === 0}
          isLast={visibleIndices[i] === numBars - 1}
        />
      ))}
    </Stack>
  )
}

export const PregnancyProgress: React.FC<{
  stage: AssessmentStepKey
  selectStage: (k: AssessmentStepKey) => void
}> = ({ stage, selectStage }) => {
  const { progress } = useFormsView()

  return <StageProgressView bars={progress} stage={stage} selectStage={selectStage} />
}
